import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PlayCircle, CheckCircle2, AlertTriangle, GraduationCap } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { fmtDate } from "@/lib/format";

export default function TrainingHome() {
    const [week, setWeek] = useState(null);
    const [history, setHistory] = useState([]);
    const [err, setErr] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const [w, h] = await Promise.all([
                    api.get("/training/my-week"),
                    api.get("/training/history"),
                ]);
                setWeek(w.data);
                setHistory(h.data.assignments);
            } catch (e) {
                setErr(apiErrorMessage(e));
            }
        })();
    }, []);

    return (
        <div className="page-shell">
            <header className="fade-up">
                <p className="eyebrow">Training</p>
                <h1 className="h1 mt-3" data-testid="th-title">This week's training</h1>
                <p className="text-muted mt-2 max-w-[640px]">
                    Watch the assigned modules in full to stay compliant. Each week's modules are due by Friday.
                </p>
            </header>

            {err && (
                <div className="brand-card mt-4" style={{ borderColor: "var(--brand-red)", color: "var(--brand-red)" }}>
                    {err}
                </div>
            )}

            <div className="brand-card mt-6 fade-up" data-testid="th-week-card">
                {!week ? (
                    <p className="text-muted">Loading…</p>
                ) : week.assignments.length === 0 ? (
                    <p className="text-muted" data-testid="th-empty">
                        Nothing assigned this week — nice. Check back Monday.
                    </p>
                ) : (
                    <ul className="flex flex-col gap-3">
                        {week.assignments.map((a) => (
                            <li
                                key={a.id}
                                className="flex items-center justify-between gap-3 p-3 rounded-md"
                                style={{ background: "var(--brand-cream)" }}
                                data-testid={`th-assignment-${a.id}`}
                            >
                                <div className="min-w-0">
                                    <p className="font-display font-bold flex items-center gap-2">
                                        {a.status === "complete" ? (
                                            <CheckCircle2 size={16} style={{ color: "var(--brand-success)" }} />
                                        ) : a.status === "overdue" ? (
                                            <AlertTriangle size={16} style={{ color: "var(--brand-red)" }} />
                                        ) : (
                                            <GraduationCap size={16} style={{ color: "var(--brand-red)" }} />
                                        )}
                                        {a.module?.title}
                                    </p>
                                    <p className="text-xs text-muted">
                                        <span className="capitalize">{a.module?.topic}</span> ·{" "}
                                        {Math.round((a.module?.duration_seconds || 0) / 60)} min · {a.percent.toFixed(0)}% watched
                                    </p>
                                </div>
                                <Link
                                    to={`/training/${a.id}`}
                                    className="btn btn-primary"
                                    data-testid={`th-watch-${a.id}`}
                                >
                                    <PlayCircle size={14} />
                                    {a.status === "complete" ? "Review" : "Watch"}
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <h2 className="h3 mt-10">History</h2>
            <div className="brand-card mt-3 p-0 overflow-hidden">
                <table className="brand-table" data-testid="th-history">
                    <thead>
                        <tr>
                            <th>Module</th>
                            <th>Topic</th>
                            <th>Week</th>
                            <th>%</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {history.length === 0 && (
                            <tr><td colSpan={5} className="text-muted text-sm" style={{ padding: "1rem" }}>No history yet.</td></tr>
                        )}
                        {history.map((a) => (
                            <tr key={a.id} style={{ cursor: "default" }}>
                                <td>{a.module?.title || "—"}</td>
                                <td className="capitalize text-sm">{a.module?.topic}</td>
                                <td className="text-sm">{fmtDate(a.week_start)}</td>
                                <td className="text-right font-mono">{a.percent.toFixed(0)}%</td>
                                <td><span className={`badge-pill ${a.status === "complete" ? "badge-success" : a.status === "overdue" ? "badge-error" : "badge-warning"}`}>{a.status.replace("_"," ")}</span></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
